// Custom elements + behaviors for the PopFerry product details panel.
// - <popferry-product-details>: keeps the "COLOR: <name>" label in sync with the selected swatch
// - <popferry-size-guide>: open/close modal, in/cm unit toggle
// - <details data-popferry-accordion>: lets only one accordion stay open at a time within its group

class PopferryProductDetails extends HTMLElement {
  connectedCallback() {
    this.#syncSelectedColor();
    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onClick);
    // Capture phase fires before variant-picker's bubble-phase change handler,
    // which is what we need: we mutate the size radio first so the picker reads
    // the corrected (color, size) when it builds the section-render request.
    this.addEventListener('change', this.#interceptColorChange, true);
  }

  /** @type {Array<{id:number, color:string, size:string, available:boolean}> | null} */
  #variants = null;

  #getVariants() {
    if (this.#variants !== null) return this.#variants;
    const script = this.querySelector('script[data-popferry-variants]');
    if (!script || !script.textContent) {
      this.#variants = [];
      return this.#variants;
    }
    try {
      const parsed = JSON.parse(script.textContent);
      this.#variants = Array.isArray(parsed) ? parsed : [];
    } catch (_e) {
      this.#variants = [];
    }
    return this.#variants;
  }

  #isMediumName(name) {
    const lower = String(name || '').toLowerCase().trim();
    return lower === 'medium' || lower === 'm';
  }

  #interceptColorChange = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (event.target.type !== 'radio') return;
    const colorFieldset = event.target.closest('.popferry-pd__option--color');
    if (!colorFieldset) return;

    const sizeFieldset = this.querySelector('.popferry-pd__option--size');
    if (!sizeFieldset) return; // single-axis product: nothing to do

    const newColor = event.target.value;
    const currentSizeInput = /** @type {HTMLInputElement | null} */ (
      sizeFieldset.querySelector('input[type="radio"]:checked')
    );
    if (!currentSizeInput) return;
    const currentSize = currentSizeInput.value;

    const variants = this.#getVariants();
    if (!variants.length) return;

    const currentSizeOk = variants.some(
      (v) => v.color === newColor && v.size === currentSize && v.available === true
    );
    if (currentSizeOk) return;

    // (newColor, currentSize) is missing or sold-out — pick a fallback.
    let fallback = variants.find(
      (v) => v.color === newColor && v.available === true && this.#isMediumName(v.size)
    );
    if (!fallback) fallback = variants.find((v) => v.color === newColor && v.available === true);
    if (!fallback) fallback = variants.find((v) => v.color === newColor);
    if (!fallback) return;

    if (fallback.size === currentSize) return;

    const escaped = window.CSS && typeof CSS.escape === 'function' ? CSS.escape(fallback.size) : fallback.size;
    const targetSizeInput = /** @type {HTMLInputElement | null} */ (
      sizeFieldset.querySelector(`input[type="radio"][value="${escaped}"]`)
    );
    if (!targetSizeInput) return;

    const variantPicker = /** @type {any} */ (this.querySelector('variant-picker'));
    if (variantPicker && typeof variantPicker.updateSelectedOption === 'function') {
      variantPicker.updateSelectedOption(targetSizeInput);
    } else {
      targetSizeInput.checked = true;
    }
  };

  #onChange = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (event.target.type !== 'radio') return;
    this.#syncSelectedColor();
  };

  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest('[data-popferry-open-size-guide]');
    if (trigger) {
      event.preventDefault();
      const dialog = document.querySelector('popferry-size-guide');
      if (dialog instanceof PopferrySizeGuide) dialog.open();
      return;
    }

    const summary = target.closest('summary');
    if (summary && summary.parentElement?.matches('[data-popferry-accordion]')) {
      // Close siblings on the next tick so <details> default toggle has applied.
      requestAnimationFrame(() => {
        const opening = summary.parentElement;
        if (!opening?.hasAttribute('open')) return;
        const group = this.querySelectorAll('[data-popferry-accordion]');
        for (const row of group) {
          if (row !== opening) row.removeAttribute('open');
        }
      });
    }
  };

  #syncSelectedColor() {
    const colorFieldset = this.querySelector('.popferry-pd__option--color');
    const label = this.querySelector('[data-popferry-selected-color]');
    if (!colorFieldset || !label) return;
    const checked = colorFieldset.querySelector('input[type="radio"]:checked');
    if (checked instanceof HTMLInputElement) {
      label.textContent = checked.value;
    }
  }
}

class PopferrySizeGuide extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.#bindUnits();
    this.#previouslyFocused = null;
  }

  open() {
    this.#previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
    const dialog = this.querySelector('.popferry-size-guide__dialog');
    if (dialog instanceof HTMLElement) dialog.focus({ preventScroll: true });
    document.addEventListener('keydown', this.#onKeyDown);
  }

  close() {
    this.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
    document.removeEventListener('keydown', this.#onKeyDown);
    if (this.#previouslyFocused) this.#previouslyFocused.focus({ preventScroll: true });
  }

  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest('[data-popferry-close]')) {
      this.close();
    }
  };

  #onKeyDown = (event) => {
    if (event.key === 'Escape') this.close();
  };

  #bindUnits() {
    const buttons = this.querySelectorAll('[data-popferry-unit]');
    if (!buttons.length) return;
    buttons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const unit = btn.getAttribute('data-popferry-unit');
        if (!unit) return;
        buttons.forEach((b) => {
          const isActive = b === btn;
          b.classList.toggle('is-active', isActive);
          b.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });
        this.querySelectorAll('[data-popferry-unit-panel]').forEach((panel) => {
          if (!(panel instanceof HTMLElement)) return;
          panel.hidden = panel.getAttribute('data-popferry-unit-panel') !== unit;
        });
      });
    });
  }

  #previouslyFocused = null;
}

if (!customElements.get('popferry-product-details')) {
  customElements.define('popferry-product-details', PopferryProductDetails);
}
if (!customElements.get('popferry-size-guide')) {
  customElements.define('popferry-size-guide', PopferrySizeGuide);
}
